"use client";

import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { queryKeys } from "@/lib/query-keys";

import { devicesRepository } from "@/features/devices/api/devices-repository";
import type {
  ShareDevicesInput,
  DeviceBucketData,
  LaunchAutomationInput,
  LoadStatsPeriod,
  MarketPublishInput,
  MarketSearchParams,
  RentDeviceInput,
  SuccessRatesParams,
} from "@/features/devices/types";

/** Default fleet page size (mirrors the legacy `PageSizeSelector` default of
 * 10). Shared so the fleet's paginated query and the `ppaDevicePresent` probe
 * use the same query key and dedupe into a single fetch. */
export const DEFAULT_DEVICE_PAGE_SIZE = 10;

/** The owner's device fleet (`/devices` "Devices" tab). `select` narrows the
 * `{ devices, ppaDevicePresent, totalDevices }` result back to `Device[]`, so
 * every existing consumer keeps seeing the plain devices array. `pageSize` is
 * optional — omitted (buckets/proxy/automations/live) means "no limit". */
export function useDevices(page = 1, pageSize?: number) {
  return useQuery({
    queryKey: queryKeys.devices.list(page, pageSize),
    queryFn: () => devicesRepository.listDevices(page, pageSize),
    placeholderData: keepPreviousData,
    select: (data) => data.devices,
  });
}

/** Whether the account owns a PPA/market device — gates the "Market Statistics"
 * tab. Shares `useDevices`' query key + fn, so it adds no extra fetch. */
export function useDevicesPpaPresent(page = 1, pageSize?: number) {
  return useQuery({
    queryKey: queryKeys.devices.list(page, pageSize),
    queryFn: () => devicesRepository.listDevices(page, pageSize),
    placeholderData: keepPreviousData,
    select: (data) => data.ppaDevicePresent,
  });
}

/** Total device count on the account — gates the page-size selector (legacy
 * shows it above 10). Shares the fleet query key, so no extra fetch. */
export function useDevicesTotal(page = 1, pageSize?: number) {
  return useQuery({
    queryKey: queryKeys.devices.list(page, pageSize),
    queryFn: () => devicesRepository.listDevices(page, pageSize),
    placeholderData: keepPreviousData,
    select: (data) => data.totalDevices,
  });
}

/** Device×job success-rate matrix (`/devices` "Success rates" tab). */
export function useDeviceSuccessRates(params: SuccessRatesParams) {
  return useQuery({
    queryKey: queryKeys.devices.successRates(params),
    queryFn: () => devicesRepository.getSuccessRates(params),
    placeholderData: keepPreviousData,
  });
}

/** Fleet utilization (`/devices` "Stats" tab). */
export function useDeviceLoadStats(period: LoadStatsPeriod) {
  return useQuery({
    queryKey: queryKeys.devices.loadStats(period),
    queryFn: () => devicesRepository.getLoadStats(period),
    placeholderData: keepPreviousData,
  });
}

export function useDeleteDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (deviceId: string) =>
      devicesRepository.deleteDevice(deviceId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.devices.all });
    },
  });
}

/** List selected devices on the rental market. Needs the full publish body
 * (payment type, uptime guarantee, prices) — see `MarketPublishInput`. */
export function usePublishToMarket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MarketPublishInput) =>
      devicesRepository.publishToMarket(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.devices.all });
    },
  });
}

/** Delist selected devices. Revokes any active rentals server-side. */
export function useUnpublishFromMarket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (deviceIds: string[]) =>
      devicesRepository.unpublishFromMarket(deviceIds),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.devices.all });
    },
  });
}

export function useAddDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (hashcode: string) => devicesRepository.addDevice(hashcode),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.devices.all });
    },
  });
}

export function useShareDevices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ShareDevicesInput) =>
      devicesRepository.shareDevices(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.devices.all });
    },
  });
}

/** Marketplace search (`/devices/marketplace`). */
export function useMarketSearch(params: MarketSearchParams) {
  return useQuery({
    queryKey: queryKeys.devices.market(params),
    queryFn: () => devicesRepository.searchMarket(params),
    placeholderData: keepPreviousData,
  });
}

export function useRentDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RentDeviceInput) =>
      devicesRepository.rentDevice(input),
    onSuccess: () => {
      // A rental changes both the marketplace availability and your fleet.
      void qc.invalidateQueries({ queryKey: queryKeys.devices.all });
    },
  });
}

/** Device settings page (`/devices/[deviceId]/settings`). */
export function useDeviceSettings(deviceId: string) {
  return useQuery({
    queryKey: queryKeys.devices.settings(deviceId),
    queryFn: () => devicesRepository.getSettings(deviceId),
    enabled: Boolean(deviceId),
  });
}

export function useGenerateTriggerLink(deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (label: string) =>
      devicesRepository.generateTriggerLink(deviceId, label),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.devices.settings(deviceId),
      });
    },
  });
}

export function useDeleteTriggerLink(deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (triggerLinkId: string) =>
      devicesRepository.deleteTriggerLink(deviceId, triggerLinkId),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.devices.settings(deviceId),
      });
    },
  });
}

export function useSetRotationConfig(deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      minIntervalSeconds,
      autoTriggerMinutes,
    }: {
      minIntervalSeconds: number;
      autoTriggerMinutes: number;
    }) => {
      await devicesRepository.setMinInterval(deviceId, minIntervalSeconds);
      await devicesRepository.setAutoTrigger(deviceId, autoTriggerMinutes);
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.devices.settings(deviceId),
      });
    },
  });
}

export function useTriggerLogs(deviceId: string, page: number, limit = 20) {
  return useQuery({
    queryKey: queryKeys.devices.triggerLogs(deviceId, page),
    queryFn: () =>
      devicesRepository.getTriggerLogs(deviceId, (page - 1) * limit, limit),
    enabled: Boolean(deviceId),
    placeholderData: keepPreviousData,
  });
}

/** Trigger-history accordion on the settings page — skip/append pagination
 * (mirrors the legacy infinite-scroll: fetch `limit` at a time, load more as the
 * list scrolls). `enabled` gates the first fetch until the accordion is opened. */
export function useTriggerLogsInfinite(
  deviceId: string,
  enabled: boolean,
  limit = 100,
) {
  return useInfiniteQuery({
    queryKey: [...queryKeys.devices.triggerLogs(deviceId, 0), "infinite"],
    queryFn: ({ pageParam }) =>
      devicesRepository.getTriggerLogs(deviceId, pageParam, limit),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.logs.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    enabled: enabled && Boolean(deviceId),
  });
}

// --- Buckets tab -----------------------------------------------------------

/** A single device's data bucket; fetched lazily when its card is expanded. */
export function useDeviceBucket(remoteDeviceId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.devices.bucket(remoteDeviceId),
    queryFn: () => devicesRepository.getBucket(remoteDeviceId),
    enabled: enabled && Boolean(remoteDeviceId),
  });
}

export function useSaveBucket(remoteDeviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: DeviceBucketData) =>
      devicesRepository.saveBucket(remoteDeviceId, data),
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.devices.bucket(remoteDeviceId), data);
    },
  });
}

export function useDeleteBucket(remoteDeviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => devicesRepository.deleteBucket(remoteDeviceId),
    onSuccess: () => {
      qc.setQueryData(queryKeys.devices.bucket(remoteDeviceId), {});
    },
  });
}

// --- Proxy tab -------------------------------------------------------------

/** All of the user's proxy connections (`/devices` "Proxy" tab). */
export function useProxyConnections() {
  return useQuery({
    queryKey: queryKeys.devices.proxyConnections(),
    queryFn: () => devicesRepository.listProxyConnections(),
  });
}

export function useCreateProxyConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, name }: { deviceId: string; name?: string }) =>
      devicesRepository.createProxyConnection(deviceId, name),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.devices.proxyConnections(),
      });
    },
  });
}

export function useDeleteProxyConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: string) =>
      devicesRepository.deleteProxyConnection(connectionId),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.devices.proxyConnections(),
      });
    },
  });
}

export function useSetProxyAssociation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      connectionId,
      deviceId,
    }: {
      connectionId: string;
      deviceId: string;
    }) => devicesRepository.setProxyAssociation(connectionId, deviceId),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.devices.proxyConnections(),
      });
    },
  });
}

export function useRotateProxyPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: string) =>
      devicesRepository.rotateProxyPassword(connectionId),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.devices.proxyConnections(),
      });
    },
  });
}

export function useProxyBlacklist(connectionId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.devices.proxyBlacklist(connectionId),
    queryFn: () => devicesRepository.listProxyBlacklist(connectionId),
    enabled: enabled && Boolean(connectionId),
  });
}

export function useAddProxyBlacklist(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (domains: string[]) =>
      devicesRepository.addProxyBlacklist(connectionId, domains),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.devices.proxyBlacklist(connectionId),
      });
    },
  });
}

export function useRemoveProxyBlacklist(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (domains: string[]) =>
      devicesRepository.removeProxyBlacklist(connectionId, domains),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.devices.proxyBlacklist(connectionId),
      });
    },
  });
}

// --- Notifications tab -----------------------------------------------------

export function useDeviceNotifications(page: number, limit = 10) {
  return useQuery({
    queryKey: queryKeys.devices.notifications(page),
    queryFn: () => devicesRepository.listDeviceNotifications(page, limit),
    placeholderData: keepPreviousData,
  });
}

export function useMarkNotificationsRead() {
  return useMutation({
    mutationFn: (notificationIds: string[]) =>
      devicesRepository.markNotificationsRead(notificationIds),
  });
}

// --- Automations tab -------------------------------------------------------

export function useLaunchAutomation() {
  return useMutation({
    mutationFn: (input: LaunchAutomationInput) =>
      devicesRepository.launchAutomation(input),
  });
}
